import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  MapPin,
  Calendar,
  Users,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  X,
  Send,
  User,
  Shield,
  FileText,
  ArrowLeft,
  Plane,
  Luggage,
  Timer,
  DollarSign,
  Star,
  Phone,
  Mail,
  ShoppingBasket,
  Info
} from 'lucide-react';
import { tripsAPI, feesAPI, applicationsAPI } from '../services/api';
import { useThemeColors } from '../theme/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import toast from 'react-hot-toast';

const TripDetails = () => {
  const colors = useThemeColors();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [feeEstimate, setFeeEstimate] = useState(null);
  const [isLoadingFee, setIsLoadingFee] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [parcelWeight, setParcelWeight] = useState('');
  const [declaredAmount, setDeclaredAmount] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const { data: trip, isLoading, error } = useQuery(
    ['trip', id],
    async () => {
      // setTimeout(async () => {
        const response = await tripsAPI.getTripDetails(id);
        return response.data.data.trip;
      // }, 3000);
    },
    {
      enabled: !!id,
    }
  );

  const queryClient = useQueryClient();

  // Fee calculation effect
  useEffect(() => {
    const isParcelWeightValid = parcelWeight && parseFloat(parcelWeight) <= parseFloat(trip?.max_weight_kg || 0);
    const isDeclaredAmountValid = !declaredAmount || parseFloat(declaredAmount) <= parseFloat(trip?.max_declared_amount || 0);
    const isPurchasePriceValid = !purchasePrice || parseFloat(purchasePrice) <= parseFloat(trip?.max_purchase_price || 0);

    if (trip && isParcelWeightValid && isDeclaredAmountValid && isPurchasePriceValid) {
      setIsLoadingFee(true);
      const timeoutId = setTimeout(async () => {
        try {
          const estimate = await feesAPI.calculateFeeEstimate('apply_to_trip', {
            weight: parcelWeight,
            id: trip.id,
            declared_value: declaredAmount || null,
            purchase_price: purchasePrice || null,
            max_delivery_price: parseFloat(parcelWeight) * parseFloat(trip.price_per_kg),
            guaranteed_delivery: declaredAmount > 0,
            purchased: purchasePrice > 0
          });
          setFeeEstimate(estimate.data.data);
        } catch (error) {
          console.error('Fee calculation error:', error);
        } finally {
          setIsLoadingFee(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setFeeEstimate(null);
    }
  }, [parcelWeight, declaredAmount, purchasePrice, trip]);

  // Real-time validation effects
  useEffect(() => {
    const errors = {};

    if (parcelWeight && parseFloat(parcelWeight) > parseFloat(trip?.max_weight_kg || 0)) {
      errors.parcelWeight = `Weight cannot exceed ${trip?.max_weight_kg}kg`;
    }

    if (declaredAmount && trip?.guaranteed_delivery && parseFloat(declaredAmount) > parseFloat(trip?.max_declared_amount || 0)) {
      errors.declaredAmount = `Amount cannot exceed ETB ${trip?.max_declared_amount}`;
    }

    if (purchasePrice && trip?.purchased && parseFloat(purchasePrice) > parseFloat(trip?.max_purchase_price || 0)) {
      errors.purchasePrice = `Price cannot exceed ETB ${trip?.max_purchase_price}`;
    }

    setValidationErrors(errors);
  }, [parcelWeight, declaredAmount, purchasePrice, trip]);

  // Apply to trip mutation
  const applyToTripMutation = useMutation(applicationsAPI.createApplication, {
    onSuccess: () => {
      toast.success('Application submitted successfully!');
      setShowApplyModal(false);
      setParcelWeight('');
      setDeclaredAmount('');
      setPurchasePrice('');
      setFeeEstimate(null);
      queryClient.invalidateQueries('trip');
    },
    onError: (error) => {
      console.error('Application submission error:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to submit application';
      toast.error(errorMessage);
    },
  });
  // Less robust approach
  const confirmApplyToTrip = async (data) => {
    try {
      setIsApplying(true);
      const response = await applicationsAPI.createApplication(data);
      if (response.status === 201) {
        toast.success('Application submitted successfully!');
        setShowApplyModal(false)
        setPurchasePrice('')
        setParcelWeight('')
        setDeclaredAmount('')
      }
      else {
        toast.error(response.data.message)
      }
    } catch (error) {
      console.error(error)
      toast.error(error.response.data.message);
    } finally {
      setIsApplying(false);
    }
  };

  const handleApplyToTrip = async () => {
    if (!parcelWeight || !trip) return;

    // Validate max values
    if (parseFloat(parcelWeight) > parseFloat(trip.max_weight_kg)) {
      toast.error(`Parcel weight cannot exceed ${trip.max_weight_kg}kg`);
      return;
    }
    if (declaredAmount && parseFloat(declaredAmount) > parseFloat(trip.max_declared_amount)) {
      toast.error(`Declared amount cannot exceed ETB ${trip.max_declared_amount}`);
      return;
    }
    if (purchasePrice && parseFloat(purchasePrice) > parseFloat(trip.max_purchase_price)) {
      toast.error(`Purchase price cannot exceed ETB ${trip.max_purchase_price}`);
      return;
    }

    setIsApplying(true);
    try {
      confirmApplyToTrip({
        target_type: 'trip',
        target_id: trip.id,
        purchase_price: purchasePrice,
        declared_value: declaredAmount,
        weight: parcelWeight,
        total_cost: feeEstimate.total_cost,
        message: `I would like to apply for your trip from ${trip.origin_city} to ${trip.destination_city} with ${parcelWeight}kg parcel.`
      })
    } finally {
      setIsApplying(false);
    }
  };

  const handleApply = () => {
    setShowApplyModal(true);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary mb-4"></div>
          <p className="text-theme-secondary font-medium">Loading trip details...</p>
        </div>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-theme-error/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="h-10 w-10" style={{ color: colors.error[600] }} />
            </div>
            <h1 className="text-2xl font-bold text-theme-primary mb-4">Trip Not Found</h1>
            <p className="text-theme-secondary mb-8 max-w-md mx-auto">
              The trip you're looking for doesn't exist or has been removed.
            </p>
            <button
              onClick={handleGoBack}
              className="bg-theme-primary text-white px-8 py-3 rounded-xl font-medium hover:bg-theme-primary-hover transition-all duration-200 flex items-center mx-auto"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const daysUntilDeparture = trip.departure_date
    ? Math.ceil((new Date(trip.departure_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  const tripDuration = trip.departure_date && trip.arrival_date
    ? Math.ceil((new Date(trip.arrival_date) - new Date(trip.departure_date)) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <SEO 
          title={`Trip from ${trip?.origin_city || 'Origin'} to ${trip?.destination_city || 'Destination'}`} 
          description={`Travel from ${trip?.origin_city || 'origin'} to ${trip?.destination_city || 'destination'} on ${trip?.departure_date || 'scheduled date'}. Available for parcel delivery on TripTrot`}
          keywords={['trip details', 'travel route', 'parcel delivery trip', 'travel schedule']}
          canonical={`/trips/${id}`}
          structuredData={{
            "@context": "https://schema.org",
            "@type": "Trip",
            "name": `Trip from ${trip?.origin_city || 'Origin'} to ${trip?.destination_city || 'Destination'}`,
            "description": `Travel from ${trip?.origin_city || 'origin'} to ${trip?.destination_city || 'destination'}`,
            "departureDate": trip?.departure_date || "",
            "arrivalDate": trip?.arrival_date || "",
            "provider": {
              "@type": "Person",
              "name": trip?.user?.first_name || "Traveler"
            }
          }}
        />

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleGoBack}
            className="flex items-center text-theme-secondary hover:text-theme-primary transition-colors mb-6 group"
          >
            <ArrowLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Trips</span>
          </button>

        </div>

        {/* Main Content */}
        <div className="space-y-6">

          {/* Route Overview Card */}
          <div className="bg-theme-card rounded-2xl border border-theme-light p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-theme-primary flex items-center">
                <MapPin className="h-6 w-6 mr-3" style={{ color: colors.primary[500] }} />
                Route Overview
              </h2>
            </div>

            {/* Modern Route Display */}
            <div className="relative">
              <div className="flex items-center justify-between">
                {/* Origin */}
                <div className="flex-1 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-theme-success/20 to-theme-success/10 rounded-2xl mb-3 border border-theme-success/20">
                    <MapPin className="h-8 w-8" style={{ color: colors.success[600] }} />
                  </div>
                  <h3 className="font-bold text-theme-primary text-lg mb-1">{trip.origin_city}</h3>
                  <p className="text-sm text-theme-secondary">{trip.origin_country}</p>
                </div>

                {/* Flight Path with Arrow */}
                <div className="flex-1 flex items-center justify-center px-4">
                  <div className="flex items-center w-full max-w-xs">
                    <div className="flex-1 h-px bg-gradient-to-r from-theme-success/30 to-theme-primary/50"></div>
                    <div className="mx-3 p-2 bg-theme-primary/10 rounded-full border border-theme-primary/20">
                      <Plane className="h-6 w-6" style={{ color: colors.primary[600] }} />
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-l from-theme-error/30 to-theme-primary/50"></div>
                  </div>
                </div>

                {/* Destination */}
                <div className="flex-1 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-theme-error/20 to-theme-error/10 rounded-2xl mb-3 border border-theme-error/20">
                    <MapPin className="h-8 w-8" style={{ color: colors.error[600] }} />
                  </div>
                  <h3 className="font-bold text-theme-primary text-lg mb-1">{trip.destination_city}</h3>
                  <p className="text-sm text-theme-secondary">{trip.destination_country}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Trip Details Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Schedule Card */}
            <div className="bg-theme-card rounded-2xl border border-theme-light p-6 shadow-sm">
              <h3 className="text-lg font-bold text-theme-primary mb-6 flex items-center">
                <Calendar className="h-5 w-5 mr-2" style={{ color: colors.primary[500] }} />
                Travel Schedule
              </h3>

              <div className="space-y-4">
                {/* Departure */}
                <div className="relative">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-theme-primary/20 to-theme-primary/10 rounded-xl flex items-center justify-center border border-theme-primary/20">
                        <Plane className="h-6 w-6" style={{ color: colors.primary[600] }} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-theme-secondary uppercase tracking-wide">Departure</span>
                        {/* <span className="text-xs text-theme-secondary">
                          {trip.departure_date ? new Date(trip.departure_date).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : ''}
                        </span> */}
                      </div>
                      <div className="text-lg font-bold text-theme-primary">
                        {trip.departure_date ? new Date(trip.departure_date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'short',
                          day: 'numeric'
                        }) : 'TBD'}
                      </div>
                      <div className="text-sm text-theme-secondary mt-1">
                        {trip.origin_city}, {trip.origin_country}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Connecting Line */}
                <div className="flex justify-center py-2">
                  <div className="w-px h-8 bg-gradient-to-b from-theme-primary/30 to-theme-success/30"></div>
                </div>

                {/* Arrival */}
                <div className="relative">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-theme-success/20 to-theme-success/10 rounded-xl flex items-center justify-center border border-theme-success/20">
                        <CheckCircle className="h-6 w-6" style={{ color: colors.success[600] }} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-theme-secondary uppercase tracking-wide">Arrival</span>
                        {/* <span className="text-xs text-theme-secondary">
                          {trip.arrival_date ? new Date(trip.arrival_date).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : ''}
                        </span> */}
                      </div>
                      <div className="text-lg font-bold text-theme-primary">
                        {trip.arrival_date ? new Date(trip.arrival_date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'short',
                          day: 'numeric'
                        }) : 'TBD'}
                      </div>
                      <div className="text-sm text-theme-secondary mt-1">
                        {trip.destination_city}, {trip.destination_country}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Duration Badge */}
                {trip.arrival_date && trip.departure_date && (
                  <div className="mt-6 text-center">
                    <div className="inline-flex items-center px-4 py-2 bg-theme-primary/10 rounded-full border border-theme-primary/20">
                      <Clock className="h-4 w-4 mr-2" style={{ color: colors.primary[600] }} />
                      <span className="text-sm font-semibold text-theme-primary">
                        {Math.ceil((new Date(trip.arrival_date) - new Date(trip.departure_date)) / (1000 * 60 * 60 * 24))} days
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Capacity & Pricing Card */}
            <div className="bg-theme-card rounded-2xl border border-theme-light p-6 shadow-sm">
              <h3 className="text-lg font-bold text-theme-primary mb-6 flex items-center">
                <Package className="h-5 w-5 mr-2" style={{ color: colors.primary[500] }} />
                Capacity & Pricing
              </h3>

              {/* Capacity Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="text-center p-4 bg-gradient-to-br from-theme-warning/10 to-theme-warning/5 rounded-xl border border-theme-warning/20">
                  <Package className="h-7 w-7 mx-auto mb-2" style={{ color: colors.warning[600] }} />
                  <div className="text-xs text-theme-secondary uppercase tracking-wide mb-1">Parcels</div>
                  <div className="text-2xl font-bold text-theme-primary">{trip.max_parcels}</div>
                  <div className="text-xs text-theme-secondary">max capacity</div>
                </div>

                <div className="text-center p-4 bg-gradient-to-br from-theme-neutral/10 to-theme-neutral/5 rounded-xl border border-theme-neutral/20">
                  <Luggage className="h-7 w-7 mx-auto mb-2" style={{ color: colors.neutral[600] }} />
                  <div className="text-xs text-theme-secondary uppercase tracking-wide mb-1">Weight</div>
                  <div className="text-2xl font-bold text-theme-primary">{trip.max_weight_kg}</div>
                  <div className="text-xs text-theme-secondary">kg limit</div>
                </div>
              </div>

              {/* Pricing Section */}
              <div className="space-y-3">
                <div className="p-4 bg-gradient-to-r from-theme-primary/10 to-theme-primary/5 rounded-xl border border-theme-primary/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-theme-secondary">Price per kg</div>
                      <div className="text-xs text-theme-secondary">ETB currency</div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-theme-primary">{trip.price_per_kg}</div>
                      <div className="text-xs text-theme-secondary">ETB</div>
                    </div>
                  </div>
                </div>

                {/* Availability Status */}
                <div className="flex items-center justify-center p-3 bg-theme-success/5 rounded-lg border border-theme-success/20">
                  <CheckCircle className="h-4 w-4 mr-2" style={{ color: colors.success[600] }} />
                  <span className="text-sm font-medium text-theme-success">Available for booking</span>
                </div>
              </div>
            </div>

          </div>

          {/* Services & Features */}
          <div className="bg-theme-card rounded-2xl border border-theme-light p-6 shadow-sm">
            <h3 className="text-lg font-bold text-theme-primary mb-6 flex items-center">
              <Shield className="h-5 w-5 mr-2" style={{ color: colors.primary[500] }} />
              Services & Features
            </h3>

            <div className="space-y-4">
              {/* Delivery Service */}
              <div className="p-4 bg-gradient-to-r from-theme-success/10 to-theme-success/5 rounded-xl border border-theme-success/20">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-theme-success/20 rounded-lg flex items-center justify-center border border-theme-success/30">
                      <CheckCircle className="h-5 w-5" style={{ color: colors.success[600] }} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-theme-primary mb-1">
                      {trip.guaranteed_delivery ? 'Guaranteed Delivery' : 'Standard Delivery'}
                    </div>
                    <div className="text-sm text-theme-secondary">
                      {trip.guaranteed_delivery
                        ? `Coverage up to ETB ${trip.max_declared_amount}`
                        : 'The traveller does not insure your parcel'
                      }
                    </div>
                  </div>
                </div>
              </div>

              {/* Purchase Service */}
              <div className="p-4 bg-gradient-to-r from-theme-primary/10 to-theme-primary/5 rounded-xl border border-theme-primary/20">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-theme-primary/20 rounded-lg flex items-center justify-center border border-theme-primary/30">
                      <ShoppingBasket className="h-5 w-5" style={{ color: colors.primary[600] }} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-theme-primary mb-1">
                      {trip.purchased ? 'Purchase Service' : 'No Purchase Service'}
                    </div>
                    <div className="text-sm text-theme-secondary">
                      {trip.purchased
                        ? `The traveller accepts purchase requests up to ETB ${trip.max_purchase_price}`
                        : 'The traveller does not accept purchase requests'
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Traveler Profile */}
          <div className="bg-theme-card rounded-2xl border border-theme-light p-6 shadow-sm">
            <h3 className="text-lg font-bold text-theme-primary mb-6 flex items-center">
              <User className="h-5 w-5 mr-2" style={{ color: colors.primary[500] }} />
              Traveler Profile
            </h3>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-theme-primary/20 to-theme-primary/10 rounded-xl flex items-center justify-center border-2 border-theme-primary/20">
                  <User className="h-8 w-8" style={{ color: colors.primary[600] }} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-theme-primary text-xl mb-2">
                  {trip.traveller?.name || 'Anonymous Traveler'}
                </h4>
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center px-3 py-1 bg-theme-warning/10 rounded-full border border-theme-warning/20">
                    <Star className="h-4 w-4 mr-1" style={{ color: colors.warning[500] }} />
                    <span className="text-sm font-medium text-theme-primary">
                      {trip.traveller?.reputation_score || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center px-3 py-1 bg-theme-success/10 rounded-full border border-theme-success/20">
                    <CheckCircle className="h-4 w-4 mr-1" style={{ color: colors.success[600] }} />
                    <span className="text-sm font-medium text-theme-success">Verified</span>
                  </div>
                </div>
                <div className="mt-3 text-sm text-theme-secondary">
                  Trusted traveler with proven delivery track record
                </div>
              </div>
            </div>
          </div>

          {/* Trip Analytics */}
          <div className="bg-theme-card rounded-2xl border border-theme-light p-6 shadow-sm">
            <h3 className="text-lg font-bold text-theme-primary mb-6 flex items-center">
              <Users className="h-5 w-5 mr-2" style={{ color: colors.primary[500] }} />
              Trip Analytics
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="text-center p-5 bg-gradient-to-br from-theme-primary/10 to-theme-primary/5 rounded-xl border border-theme-primary/20">
                <div className="text-3xl font-bold text-theme-primary mb-1">{(trip.applications?.length || 0)}</div>
                <div className="text-xs text-theme-secondary uppercase tracking-wide font-medium">Applications</div>
                <div className="text-xs text-theme-secondary mt-1">Interest shown</div>
              </div>

              <div className="text-center p-5 bg-gradient-to-br from-theme-success/10 to-theme-success/5 rounded-xl border border-theme-success/20">
                <div className="text-3xl font-bold text-theme-success mb-1">{trip.agreements && trip.agreements.length ? trip.agreements.length : 0}</div>
                <div className="text-xs text-theme-secondary uppercase tracking-wide font-medium">Agreements</div>
                <div className="text-xs text-theme-secondary mt-1">Confirmed bookings</div>
              </div>

              {/* <div className="text-center p-5 bg-gradient-to-br from-theme-warning/10 to-theme-warning/5 rounded-xl border border-theme-warning/20">
                <div className="text-3xl font-bold text-theme-warning mb-1">{(trip.current_parcels || 0)}</div>
                <div className="text-xs text-theme-secondary uppercase tracking-wide font-medium">Current</div>
                <div className="text-xs text-theme-secondary mt-1">Parcels assigned</div>
              </div> */}

              <div className="text-center p-5 bg-gradient-to-br from-theme-neutral/10 to-theme-neutral/5 rounded-xl border border-theme-neutral/20">
                <div className="text-3xl font-bold text-theme-primary mb-1">{trip.max_parcels - (trip.applications?.length || 0)}</div>
                <div className="text-xs text-theme-secondary uppercase tracking-wide font-medium">Available</div>
                <div className="text-xs text-theme-secondary mt-1">Slots remaining</div>
              </div>
            </div>
          </div>

          {/* Trip Description */}
          {trip.notes && (
            <div className="bg-theme-card rounded-2xl border border-theme-light p-6 shadow-sm">
              <h3 className="text-lg font-bold text-theme-primary mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2" style={{ color: colors.primary[500] }} />
                Trip Details & Notes
              </h3>
              <div className="p-4 bg-theme-neutral/5 rounded-lg border border-theme-light">
                <p className="text-theme-secondary leading-relaxed text-sm">{trip.notes}</p>
              </div>
            </div>
          )}

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 w-full">

            {trip.status === 'posted' && user?.id !== trip.traveller.id && (
              <button
                onClick={handleApply}
                className="w-full bg-theme-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-theme-primary-hover transition-colors duration-200 flex items-center shadow-md hover:shadow-lg justify-center border border-theme-primary hover:border-theme-primary-hover"
              >
                <Send className="h-4 w-4 mr-2" />
                <span>Apply</span>
              </button>
            )}
          </div>
        </div>

        {/* Apply Modal */}
        {showApplyModal && trip && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-theme-card rounded-lg max-w-2xl w-full border-theme-light border max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-theme-primary">Apply for Trip</h3>
                  <button
                    onClick={() => setShowApplyModal(false)}
                    className="text-theme-secondary hover:text-theme-primary transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="mb-6">
                  <h4 className="font-medium text-theme-primary mb-2">{trip.origin_city} → {trip.destination_city}</h4>
                  <p className="text-theme-secondary text-sm">
                    Departure: {new Date(trip.departure_date).toLocaleDateString()} •
                    Available Weight: {trip.available_weight_kg || trip.max_weight_kg}kg
                  </p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-theme-secondary mb-2">
                    Parcel Weight (kg) - Max: {trip.max_weight_kg}kg
                  </label>
                  <input
                    type="number"
                    value={parcelWeight}
                    onChange={(e) => {
                      const value = e.target.value;
                      const max = parseFloat(trip.max_weight_kg);
                      const clampedValue = value && parseFloat(value) > max ? max.toString() : value;
                      setParcelWeight(clampedValue);
                    }}
                    placeholder="Enter parcel weight in kg"
                    max={trip.max_weight_kg}
                    step="0.1"
                    min="0.1"
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-theme-primary focus:border-transparent ${
                      validationErrors.parcelWeight ? 'border-theme-error' : 'border-theme-light'
                    }`}
                    style={{ backgroundColor: colors.card }}
                  />
                  {validationErrors.parcelWeight && (
                    <p className="text-xs text-theme-error mt-1">{validationErrors.parcelWeight}</p>
                  )}
                  <p className="text-xs text-theme-secondary mt-1">
                    Price will be calculated as weight × ETB {trip.price_per_kg}/kg
                  </p>
                </div>

                {trip.guaranteed_delivery && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-theme-secondary mb-2">
                      Declared Amount (Optional) - Max: ETB {trip.max_declared_amount}
                    </label>
                    <input
                      type="number"
                      value={declaredAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        const max = parseFloat(trip.max_declared_amount);
                        const clampedValue = value && parseFloat(value) > max ? max.toString() : value;
                        setDeclaredAmount(clampedValue);
                      }}
                      placeholder="Enter declared value for insurance"
                      max={trip.max_declared_amount}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-theme-primary focus:border-transparent ${
                        validationErrors.declaredAmount ? 'border-theme-error' : 'border-theme-light'
                      }`}
                      style={{ backgroundColor: colors.card }}
                    />
                    {validationErrors.declaredAmount && (
                      <p className="text-xs text-theme-error mt-1">{validationErrors.declaredAmount}</p>
                    )}
                    <p className="text-xs text-theme-secondary mt-1">
                      Amount covered by guaranteed delivery insurance
                    </p>
                  </div>
                )}

                {trip.purchased && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-theme-secondary mb-2">
                      Purchase Price (Optional) - Max: ETB {trip.max_purchase_price}
                    </label>
                    <input
                      type="number"
                      value={purchasePrice}
                      onChange={(e) => {
                        const value = e.target.value;
                        const max = parseFloat(trip.max_purchase_price);
                        const clampedValue = value && parseFloat(value) > max ? max.toString() : value;
                        setPurchasePrice(clampedValue);
                      }}
                      placeholder="Enter purchase amount"
                      max={trip.max_purchase_price}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-theme-primary focus:border-transparent ${
                        validationErrors.purchasePrice ? 'border-theme-error' : 'border-theme-light'
                      }`}
                      style={{ backgroundColor: colors.card }}
                    />
                    {validationErrors.purchasePrice && (
                      <p className="text-xs text-theme-error mt-1">{validationErrors.purchasePrice}</p>
                    )}
                    <p className="text-xs text-theme-secondary mt-1">
                      Amount the traveller will purchase for you
                    </p>
                  </div>
                )}


                <div className="border-t border-theme-warning-200 pt-3 mt-3">
                  <h5 className="font-medium text-theme-primary mb-2"> Fee Breakdown</h5>
                  {isLoadingFee ? (
                    <div className="text-sm text-theme-secondary">Loading fee information...</div>
                  ) : feeEstimate ? (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-theme-secondary">Application Fee:</span>
                        <span className="font-medium text-theme-primary">
                          ETB {feeEstimate.application_fee || 0}
                        </span>
                      </div>
                      {feeEstimate.deposit_requirement !== null && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-theme-secondary">Deposit Required:</span>
                          <span className="font-medium text-theme-primary">
                            ETB {feeEstimate.deposit_requirement}
                          </span>
                        </div>
                      )}
                      {feeEstimate.delivery_fee !== null && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-theme-secondary">Delivery Fee:</span>
                          <span className="font-medium text-theme-primary">
                            ETB {feeEstimate.delivery_fee}
                          </span>
                        </div>
                      )}
                      <div className="border-t border-theme-warning-200 pt-2 mt-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-theme-primary">Total Required:</span>
                          <span className="font-bold text-theme-primary">
                            ETB {(feeEstimate.total_cost)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-theme-secondary">
                      Unable to load fee information. Please try again.
                    </p>
                  )}
                  <p className="text-xs text-theme-secondary mt-2">
                    Application fee is non-refundable. Deposit will be held until delivery completion.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowApplyModal(false)}
                    className="flex-1 px-4 py-2 border border-theme-light rounded-lg text-theme-secondary hover:bg-theme-neutral-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApplyToTrip}
                    disabled={isApplying || !parcelWeight || Object.keys(validationErrors).length > 0}
                    className="flex-1 px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-theme-primary-hover disabled:opacity-50 transition-colors"
                  >
                    {isApplying ? 'Applying...' : 'Submit Application'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default TripDetails;
